import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../builder/components/layout/AppLayout';
import Table from '../../shared/components/layout/Table';
import Button from '../../shared/components/ui/Button';
import Search from '../../shared/components/ui/Search';
import Header from '../../shared/components/layout/Header';
import Modal from '../../shared/components/ui/Modal';
import { transformData } from '../../super-admin/utils/transformData';
import AddLand from '../components/forms/AddLand';
import EditLand from '../components/forms/EditLand';
import {
  createLand,
  editLandById,
  getAllLands,
  handleCheckLandDelete,
  deleteLand
} from '../services/api/implementation/impl';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  buildReturnToFromLocation,
  navigateWithReturn
} from '../../shared/navigation';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { filterData } from '../../shared/utils/filterData';
import NoDataFound from '../components/layout/NoDataFound';
import DeleteConfirmationModal from '../components/ui/DeleteConfirmationModal';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import Loader from '../../shared/components/ui/Loader';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import { LayoutGrid, List, Pencil, Plus, Trash2 } from 'lucide-react';

/**
 * Converts a numeric value to a string with up to 3 decimals,
 * trimming trailing zeros (e.g. 1.200 → 1.2, 1.000 → 1).
 */
const formatNumber = (input) => {
  const number = parseFloat(input);
  if (isNaN(number)) {
    return 'N/A';
  }

  if (Number.isInteger(number)) {
    return number;
  }

  return parseFloat(number.toFixed(3)).toString();
};

const parseAreaType = (areaType) => {
  if (!areaType) return '';

  let type = areaType.toLowerCase().trim();
  type = type.replace('sq/', '');
  type = type.replace(/\s+/g, '');

  if (type.includes('guntha')) type = type.replace('guntha', 'gunta');
  if (type === 'gunta' || type === 'guntha') type = 'gunta';

  if (type === 'acres') type = 'acre';

  if (type === 'hectares') type = 'hectare';

  if (type === 'cent' || type === 'cents') type = 'cents';

  return type;
};

const calculateFinalArea = (mainArea, mainAreaType, subArea, subAreaType) => {
  const mArea = parseFloat(mainArea) || 0;
  const sArea = parseFloat(subArea) || 0;

  const mType = parseAreaType(mainAreaType);
  const sType = parseAreaType(subAreaType);

  let finalMainArea = mArea;
  let finalMainAreaType = mType;
  let finalSubArea = sArea;
  let finalSubAreaType = sType;

  if (sType === 'gunta') {
    // CASE 1: main area is acre
    if (mType === 'acre') {
      const gunthaPerAcre = 40; // 1 acre = 40 gunta
      // How many acres do we get from the sub-area?
      const acresFromSub = Math.floor(sArea / gunthaPerAcre);
      const leftoverGunta = sArea % gunthaPerAcre;

      finalMainArea = mArea + acresFromSub;
      finalMainAreaType = 'acre';
      finalSubArea = leftoverGunta;
      finalSubAreaType = 'gunta';
    }
    // CASE 2: main area is hectare
    else if (mType === 'hectare') {
      const gunthaPerHectare = 98.84; // 1 hectare ~ 98.84 gunta
      // Convert leftover gunta to hectares
      const totalHectaresFromSub = sArea / gunthaPerHectare;

      // The integer portion to add to main area
      const integerHectares = Math.floor(totalHectaresFromSub);
      // Fractional portion
      const fractionalHectares = totalHectaresFromSub - integerHectares;

      finalMainArea = mArea + integerHectares;
      finalMainAreaType = 'hectare';

      // leftover gunta = fractional part * 98.84
      const leftoverGunta = fractionalHectares * gunthaPerHectare;
      finalSubArea = leftoverGunta;
      finalSubAreaType = 'gunta';
    }
  }
  // CASE 3: Sub area is in cents
  else if (sType === 'cents') {
    // CASE 3.1: main area is acre
    if (mType === 'acre') {
      const centsPerAcre = 100; // 1 acre = 100 cents
      // How many acres do we get from the sub-area?
      const acresFromSub = Math.floor(sArea / centsPerAcre);
      const leftoverCents = sArea % centsPerAcre;

      finalMainArea = mArea + acresFromSub;
      finalMainAreaType = 'acre';
      finalSubArea = leftoverCents;
      finalSubAreaType = 'cents';
    }
    // CASE 3.2: main area is hectare
    else if (mType === 'hectare') {
      const centsPerHectare = 247.105; // 1 hectare ~ 247.105 cents
      // Convert leftover cents to hectares
      const totalHectaresFromSub = sArea / centsPerHectare;

      // The integer portion to add to main area
      const integerHectares = Math.floor(totalHectaresFromSub);
      // Fractional portion
      const fractionalHectares = totalHectaresFromSub - integerHectares;

      finalMainArea = mArea + integerHectares;
      finalMainAreaType = 'hectare';

      // leftover cents = fractional part * 247.105
      const leftoverCents = fractionalHectares * centsPerHectare;
      finalSubArea = leftoverCents;
      finalSubAreaType = 'cents';
    }
  }

  return { finalMainArea, finalMainAreaType, finalSubArea, finalSubAreaType };
};

const LandsList = () => {
  /**
   * Hooks
   */
  const navigate = useNavigate();
  const location = useLocation();
  const userId = Cookies.get('userId');

  /**
   * Local state
   */
  const { collapsed } = useSidebar();
  const [isOpenAddLand, setIsOpenAddLand] = useState(false);
  const [isOpenEditLand, setIsOpenEditLand] = useState(false);
  const [LandsStaticId, setLandsStaticId] = useState(null);
  const [filterLand, setFilterLand] = useState([]);
  const [landQuery, setLandQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canDelete, setCanDelete] = useState(null);
  const [resData, setResData] = useState();
  const [selectedIds, setSelectedIds] = useState([]);
  const [viewMode, setViewMode] = useState('card');

  /**
   * Meta data
   */
  const landsFormat = ['id', 'name', 'number', 'area', 'tehsil', 'actions'];

  /**
   * Data fetching
   */
  const {
    data: lands,
    refetch: refetchLands,
    isError,
    isLoading
  } = useQuery({
    queryKey: ['lands'],
    queryFn: getAllLands
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Lands List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    setFilterLand(lands);
  }, [lands]);

  /**
   * Data mutation
   */
  const { mutate: mutateLand } = useMutation({
    mutationFn: (land) => createLand(land),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      toast.success('Land created successfully');
      setIsOpenAddLand(false);
      await refetchLands(); // UI data update
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error creating new land', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error creating land');
    }
  });

  const { mutate: handleEditLand } = useMutation({
    mutationFn: (updatedLand) => {
      // Log the payload for debugging
      console.log('Updated Land Payload:', updatedLand);
      return editLandById(parseInt(LandsStaticId), updatedLand);
    },
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      toast.success('Land edited successfully');
      setIsOpenEditLand(false);
      await refetchLands();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error editing land:', err); // Log the error for debugging
      toast.error(err?.response?.data?.message || err?.message || 'Error editing land');
    }
  });

  /**
   * Helper method calls
   */
  const handleSearchChange = useCallback(
    (incomingQuery) => {
      setLandQuery(incomingQuery);
      if (incomingQuery) {
        const filtered = filterData('name', lands, incomingQuery);
        setFilterLand(filtered);
      } else {
        setFilterLand(lands);
      }
    },
    [lands]
  );

  /**
   * We prepare the data for the table by:
   *   - Collecting all khasra (or survey) numbers
   *   - Calculating final main/sub area if needed
   *   - Formatting the final area string
   */

  
  const landsFormatted = filterLand?.map((item) => {
    let allKhasraNumbers = [];

    if (Array.isArray(item?.ownerLandMappings)) {
      item.ownerLandMappings.forEach((mapping) => {
        if (Array.isArray(mapping?.owner?.ownerKhasraMappings)) {
          mapping.owner.ownerKhasraMappings.forEach((khasraMapping) => {
            allKhasraNumbers.push(khasraMapping.khasra.number);
          });
        }
      });
    }

    // Remove duplicates using Set
    allKhasraNumbers = [...new Set(allKhasraNumbers)];

    // Convert all khasra numbers to a single string
    const numbers =
      allKhasraNumbers.length > 0
        ? `${allKhasraNumbers.join(', ')} ${item?.state === 'Karnataka' || item?.state === 'Gujarat'
          ? '(survey)'
          : item?.state?.toLowerCase() === 'west bengal'
            ? '(dag)'
            : '(khasra)'
        }`
        : 'N/A';

    // Check for universal measurement on main area
    const mainUniversal = item?.totalMainAreaUniversalMeasurement ?? null;
    const subUniversal = item?.totalSubAreaUniversalMeasurement ?? null;

    // Build display string for total area
    let displayArea = 'N/A';

    if (mainUniversal) {
      // Reverse-convert: stored value (in base unit) ÷ per_unit → original user value in universal unit
      const perUnit = parseFloat(mainUniversal.per_unit) || 1;
      const displayMain = parseFloat(item?.total_mainArea) / perUnit;
      const displayMainStr = `${formatNumber(displayMain)} ${mainUniversal.name}`;

      if (subUniversal && parseFloat(item?.total_subArea) !== 0) {
        const subPerUnit = parseFloat(subUniversal.per_unit) || 1;
        const displaySub = parseFloat(item?.total_subArea) / subPerUnit;
        displayArea = `${displayMainStr}, ${formatNumber(displaySub)} ${subUniversal.name}`;
      } else {
        displayArea = displayMainStr;
      }
    } else {
      // No universal measurement — use standard conversion logic
      const { finalMainArea, finalMainAreaType, finalSubArea, finalSubAreaType } =
        calculateFinalArea(
          item?.total_mainArea,
          item?.total_mainAreaType,
          item?.total_subArea,
          item?.total_subAreaType
        );

      if (finalMainArea || finalSubArea) {
        if (parseFloat(finalSubArea) === 0) {
          displayArea = `${formatNumber(finalMainArea)} ${finalMainAreaType}`;
        } else {
          displayArea = `${formatNumber(finalMainArea)} ${finalMainAreaType}, ${formatNumber(
            finalSubArea
          )} ${finalSubAreaType}`;
        }
      }
    }

    // Append (taluk) or (tehsil)
    const tehsilDisplay =
      item?.tehsil +
      ' ' +
      (item?.state === 'Karnataka' || item?.state === 'Gujarat' ? '(taluk)' : '(tehsil)') ||
      'N/A';

    return {
      ...transformData(
        {
          ...item,
          number: numbers,
          area: displayArea,
          tehsil: tehsilDisplay
        },
        landsFormat
      ),
      // Passed to LandCard only; TableRow omits keys starting with "_"
      _state: item?.state ?? null,
      _district: item?.district ?? null
    };
  });
  const landsToRender = landsFormatted;

  /**
   * Event handlers
   */
  const handleOnSubmitAddLand = (data) => {
    mutateLand(data);
  };

  const handleToggleAddLand = useCallback(() => {
    setIsOpenAddLand((prev) => !prev);
  }, []);

  const handleToggleEditLand = useCallback((id) => {
    setLandsStaticId(id);
    setIsOpenEditLand((prev) => !prev);
  }, []);

  if (isError) {
    toast.error('Error fetching lands');
    navigate('/');
    return null;
  }

  //delete functions

  const { mutate: handleCheckDelete } = useMutation({
    mutationFn: (id) => handleCheckLandDelete(id),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async (res, id) => {
      setIsSubmitting(false);
      const land = filterLand?.find((item) => parseInt(item.id) === parseInt(id));
      const landName = land?.name || 'Land';
      if (res.canDelete) {
        setCanDelete('yes');
        setResData({ name: landName });
        setSelectedIds([parseInt(id)]);
      } else {
        // Fallback if not found
        setResData({ ...res, name: landName });
        setCanDelete('no');
      }
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error checking dependencies', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error checking dependencies');
    }
  });

  const { mutate: handleDeleteFn } = useMutation({
    mutationFn: (payload) => deleteLand(payload),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success('Land Deleted Successfully');
      refetchLands();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error deleting land', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error deleting land');
    }
  });

  const HandleDelete = (data) => {
    const payload = { ids: data };
    handleDeleteFn(payload);
  };

  const LandCard = ({ land }) => {
    const allotmentNumber = String(land?.number || 'N/A');
    const totalArea = String(land?.area || 'N/A');
    const tehsil = String(land?.tehsil || 'N/A');
    const stateName = String(land?._state ?? '').trim();
    const districtName = String(land?._district ?? '').trim();
    const subtitle =
      districtName && stateName
        ? `${districtName}, ${stateName}`
        : stateName || districtName || 'Land Record';
    const initials = String(land?.name || 'Land')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('');
    const avatarThemes = [
      { wrap: 'border-[#C9D4F3] bg-[#EEF2FF]', text: 'text-[#4F46E5]' },
      { wrap: 'border-[#BFE3F3] bg-[#E0F2FE]', text: 'text-[#0369A1]' },
      { wrap: 'border-[#BBF7D0] bg-[#DCFCE7]', text: 'text-[#15803D]' },
      { wrap: 'border-[#F5D0FE] bg-[#FAE8FF]', text: 'text-[#A21CAF]' },
      { wrap: 'border-[#FCD9A8] bg-[#FFF7ED]', text: 'text-[#B45309]' }
    ];
    const themeIndex = Number(land?.id || 0) % avatarThemes.length;
    const activeTheme = avatarThemes[themeIndex];

    return (
      <article
        role="link"
        tabIndex={0}
        aria-label={`View land: ${land?.name || 'Untitled Land'}`}
        onClick={() => navigateWithReturn(navigate, `/builder/management/land/${land?.id}`, {
                  returnTo: buildReturnToFromLocation(location)
                })}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigateWithReturn(navigate, `/builder/management/land/${land?.id}`, {
                  returnTo: buildReturnToFromLocation(location)
                });
          }
        }}
        className="group bg-white rounded-2xl border border-[#E5EAF1] p-4 flex flex-col gap-2.5 shadow-sm relative h-full cursor-pointer transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#D8DEE6] motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5] focus-visible:ring-offset-2 min-h-[220px]">
        <div className="flex-1 transition-colors duration-300 rounded-xl group-hover:bg-[#FAFBFC]/80">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3 pr-[4.5rem]">
              <div
                className={`h-14 w-14 shrink-0 rounded-xl border flex items-center justify-center ${activeTheme.wrap}`}>
                <span className={`text-lg font-semibold ${activeTheme.text}`}>{initials || 'L'}</span>
              </div>
              <div className="min-w-0">
                <h3 className="text-[20px] leading-tight font-semibold text-[#111827] truncate">
                  {land?.name || 'Untitled Land'}
                </h3>
                <p className="mt-1 text-[12px] leading-tight text-[#6B7280] truncate">{subtitle}</p>
              </div>
            </div>
            <div className="absolute top-3 right-3 flex items-center gap-1">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleEditLand(land?.id);
                }}
                className="bg-[#F2F5F8] p-1 rounded-full text-[#5B6472] hover:bg-[#E2E8F0]"
                aria-label="Edit land">
                <Pencil size={16} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCheckDelete(land?.id);
                }}
                className="bg-[#F2F5F8] p-1 rounded-full text-[#5B6472] hover:bg-[#E2E8F0]"
                aria-label="Delete land">
                <Trash2 size={16} />
              </button>
            </div>
          </div>

          <div className="mt-2 flex flex-col gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                title={allotmentNumber}
                className="inline-flex items-center rounded-full px-2.5 py-1 text-[13px] font-semibold bg-[#EAF8F2] text-[#0F766E] max-w-full truncate">
                Allotment: {allotmentNumber}
              </span>
              <span
                title={`Area: ${totalArea}`}
                className="inline-flex items-center rounded-full px-2.5 py-1 text-[14px] font-semibold bg-[#EEF2FF] text-[#4F46E5] max-w-full truncate">
                Area: {totalArea}
              </span>
            </div>
            <div className="flex items-center">
              <span
                title={tehsil}
                className="inline-flex items-center rounded-full px-2.5 py-1 text-[13px] font-semibold bg-[#F3F4F6] text-[#4B5563] max-w-full truncate">
                {tehsil}
              </span>
            </div>
          </div>
        </div>

        {/* <div className="border-t border-[#E7EBF1] mt-1 -mx-4 -mb-4">
          <div className="grid grid-cols-2">
          <button
            type="button"
            onClick={() => navigateWithReturn(navigate, `/builder/management/land/${land?.id}`, {
                  returnTo: buildReturnToFromLocation(location)
                })}
            className="h-11 text-[15px] font-semibold text-[#4D6C72] hover:bg-[#F7FAFB] transition-colors">
            View Land
          </button>
          <button
            type="button"
            onClick={() => handleToggleEditLand(land?.id)}
            className="h-11 text-[15px] font-semibold text-[#4D6C72] border-l border-[#E7EBF1] hover:bg-[#F7FAFB] transition-colors">
            Edit Land
          </button>
          </div>
        </div> */}
      </article>
    );
  };

  return (
    <>
      <Loader isVisible={isSubmitting || isLoading} />
      {isOpenAddLand && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={'Add Land'}
          onClose={handleToggleAddLand}
          width={88}
          upShift>
          <AddLand
            isSubmitting={isSubmitting}
            onSubmit={handleOnSubmitAddLand}
            onClose={handleToggleAddLand}
          />
        </Modal>
      )}

      {isOpenEditLand && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={'Edit Land'}
          onClose={handleToggleEditLand}
          width={88}
          upShift>
          <EditLand
            isSubmitting={isSubmitting}
            onSubmit={handleEditLand}
            id={LandsStaticId}
            onClose={handleToggleEditLand}
          />
        </Modal>
      )}

      {canDelete === 'no' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={`Cannot delete '${resData?.name || 'this land'}'`}
          onClose={() => setCanDelete(null)}
          width={45}>
          <div className="flex flex-col justify-center align-center ">
            <p className="text-center">
              Looks like this land has dependencies, please delete following projects to proceed
            </p>
            <div className="flex justify-start, align-middle flex-wrap bg-[#f5f7f8] p-2 rounded-[5px] mt-4">
              {resData &&
                resData.reason?.map((r, index) => (
                  <span className="mt-[2px] mr-[4px] text-[#535353]" key={index}>
                    {index + 1}.{r}
                    {index < resData.reason.length - 1 ? ', ' : ''}
                  </span>
                ))}
            </div>
          </div>
        </Modal>
      )}

      {canDelete === 'yes' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={'Confirmation'}
          onClose={() => {
            setCanDelete(null);
            setSelectedIds([]);
          }}
          width={45}>
          <DeleteConfirmationModal
            name={resData.name}
            title={'land'}
            onClose={() => setCanDelete(null)}
            data={landsFormatted}
            type={'land'}
            setCanDelete={setCanDelete}
            onDeleteClick={HandleDelete}
            setSelectedIds={setSelectedIds}
            selectedIds={selectedIds}
          />
        </Modal>
      )}

      <Layout>
        {/* Header */}
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
            }`}>
          <Header data={{ name: 'Lands' }} />
        </div>

        {/* Search and Add Button */}
        <div
          className={`flex items-center justify-between px-6 py-4 bg-white fixed top-[4rem] border-t-[2px] border-stone-100  shadow-sm  z-20  transition-all duration-300 ease-in-out ${collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
            }`}>
          <div className="flex w-full items-center justify-between gap-3">
            <Search onChange={handleSearchChange} query={landQuery} />
            <div className="inline-flex items-center rounded-xl border border-[#DCE3EA] bg-white p-1 shadow-sm shrink-0">
              <button
                type="button"
                onClick={() => setViewMode('card')}
                className={`h-9 px-4 rounded-lg inline-flex items-center justify-center gap-1.5 text-[13px] font-semibold transition-colors ${
                  viewMode === 'card'
                    ? 'bg-[#EEF2F5] text-[#617087]'
                    : 'text-[#7A879B] hover:text-[#617087]'
                }`}
                aria-label="Card view">
                <LayoutGrid size={14} />
                <span>Card View</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`h-9 px-4 rounded-lg inline-flex items-center justify-center gap-1.5 text-[13px] font-semibold transition-colors ${
                  viewMode === 'table'
                    ? 'bg-[#EEF2F5] text-[#0F4D52]'
                    : 'text-[#7A879B] hover:text-[#617087]'
                }`}
                aria-label="Table view">
                <List size={14} />
                <span>Table View</span>
              </button>
            </div>
          </div>
        </div>

        <GridWrapper width={100}>
          {/* Table content */}
          <div className="mt-[10rem] relative table-container h-[calc(100vh-11.5rem)] overflow-auto">
            <style>{`
              .table-container .table-body {
                position: static !important;
                overflow-y: visible !important;
                max-height: none !important;
                height: auto !important;
                top: auto !important;
                padding-bottom: 0 !important;
              }
              .table-container .table-container {
                height: auto !important;
              }
              .table-container > div {
                height: auto !important;
              }

            `}</style>
            {viewMode === 'card' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 pb-4">
                {landsToRender?.length === 0 ? (
                  <div className="md:col-span-2 xl:col-span-3 min-h-[calc(100vh-16rem)] flex items-center justify-center">
                    <div className="w-full max-w-[640px] rounded-2xl border border-dashed border-[#C7D3DE] bg-white min-h-[260px] flex flex-col items-center justify-center text-center p-6">
                    <p className="text-lg font-semibold text-stone-600">No lands found</p>
                    <p className="text-sm text-stone-500 mt-1 mb-4">
                      Add your first land record to get started.
                    </p>
                    <Button
                      displayType="primary"
                      onClick={handleToggleAddLand}
                      customStyle={{
                        borderRadius: '10px',
                        padding: '10px 16px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}>
                      <span className="inline-flex items-center gap-2">
                        <Plus size={16} color="white" />
                        <span>Add Land</span>
                      </span>
                    </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleToggleAddLand}
                      className=" min-h-[220px]  rounded-2xl border-2 border-dashed border-[#BFD4D8] bg-white text-[#2A565E] flex flex-col items-center justify-center gap-3 shadow-sm hover:bg-[#F7FBFC] hover:border-[#9EC5CC] transition-colors">
                      <span className="h-11 w-11 rounded-full bg-[#E8F3F5] flex items-center justify-center">
                        <Plus size={22} />
                      </span>
                      <span className="text-base font-semibold">Add Land</span>
                    </button>
                    {landsToRender?.map((land) => (
                      <LandCard key={land?.id} land={land} />
                    ))}
                  </>
                )}
              </div>
            ) : landsToRender?.length === 0 ? (
              <NoDataFound onButtonClick={handleToggleAddLand} buttonText="Add Land" />
            ) : (
              <Table
                headers={[
                  'Land Name',
                  'Allotment Number',
                  'Total Area',
                  'Adminstrative Division',
                  'Action'
                ]}
                identifier={'id'}
                handleRowClick={(id) =>
                  navigateWithReturn(navigate, `/builder/management/land/${id}`, {
                    returnTo: buildReturnToFromLocation(location)
                  })
                }
                onEdit={(id) => handleToggleEditLand(id)}
                data={landsToRender}
                isDeleteEnable={true}
                onDelete={handleCheckDelete}
                isWrap={true}
              />
            )}
          </div>
        </GridWrapper>
      </Layout>
    </>
  );
};

export default LandsList;
